import { useCallback, useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import PaymentFormDialog from "@/components/payments/PaymentFormDialog";
import ReceiptDialog from "@/components/payments/ReceiptDialog";
import { PAYMENT_MODES, PAY_STATUS_STYLE, REPORT_TYPES, fmtMoney } from "@/lib/paymentConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Plus, Search, Download, Loader2, IndianRupee, Wallet, AlertTriangle, Clock,
  MoreHorizontal, Receipt, Pencil, Trash2, CheckCircle2, Bell, FileSpreadsheet, FileText,
} from "lucide-react";

function Kpi({ icon: Icon, label, value, tint, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tint}`}><Icon className="h-4 w-4" /></span>
      </div>
      <p className="mt-2 font-display text-xl font-bold tracking-tight text-secondary">{value}</p>
    </div>
  );
}

function RevChart({ title, data, keyName, color, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <h3 className="mb-3 font-display text-sm font-semibold text-secondary">{title}</h3>
      <ResponsiveContainer width="100%" height={190}>
        <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey={keyName} tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-15} textAnchor="end" height={45} />
          <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
          <Tooltip formatter={(v) => fmtMoney(v)} cursor={{ fill: "#F1F5F9" }} />
          <Bar dataKey="amount" fill={color} radius={[5, 5, 0, 0]} maxBarSize={36} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Payments() {
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [dues, setDues] = useState(null);

  const [result, setResult] = useState({ items: [], total: 0 });
  const [loadingList, setLoadingList] = useState(true);
  const [filters, setFilters] = useState({ search: "", payment_mode: "all" });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [receiptId, setReceiptId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [reportType, setReportType] = useState("monthly");
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setFilters((f) => ({ ...f, search: searchInput })); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchOverview = useCallback(async () => {
    const [d, du] = await Promise.all([api.get("/payments/dashboard"), api.get("/payments/dues")]);
    setDash(d.data); setDues(du.data);
  }, []);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/payments", { params: { ...filters, page, page_size: pageSize } });
      setResult({ items: data.items, total: data.total });
    } finally { setLoadingList(false); }
  }, [filters, page]);

  const fetchReport = useCallback(async (type) => {
    setLoadingReport(true);
    try { const { data } = await api.get("/payments/reports", { params: { report_type: type } }); setReport(data); }
    finally { setLoadingReport(false); }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { if (tab === "reports") fetchReport(reportType); }, [tab, reportType, fetchReport]);

  const refreshAll = () => { fetchOverview(); fetchList(); };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/payments/${deleteTarget.id}`);
      toast.success("Payment deleted");
      setDeleteTarget(null);
      refreshAll();
    } finally { setDeleting(false); }
  };

  const markReminder = async (studentId) => {
    await api.patch(`/payments/dues/${studentId}/complete`);
    toast.success("Reminder marked completed");
    fetchOverview();
  };

  const exportPayments = async () => {
    try {
      const res = await api.get("/payments/export", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "sifcore_payments.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const exportReport = async (fmt) => {
    try {
      const res = await api.get("/payments/reports/export", { params: { report_type: reportType, fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${reportType}_report.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Report export failed"); }
  };

  const totalPages = Math.max(1, Math.ceil(result.total / pageSize));
  const dueCount = (b) => (dues?.[b]?.length || 0);

  return (
    <div data-testid="payments-page">
      <PageHeader
        title="Fee & Installments"
        subtitle="Record installments, track dues and generate receipts."
        actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-payment-button"><Plus className="mr-1.5 h-4 w-4" /> Add Payment</Button>}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          {!dash ? (
            <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Kpi testid="kpi-today" icon={IndianRupee} label="Today's Collection" value={fmtMoney(dash.today_collection)} tint="bg-accent/10 text-accent" />
                <Kpi testid="kpi-month" icon={Wallet} label="Monthly Collection" value={fmtMoney(dash.monthly_collection)} tint="bg-primary/10 text-primary" />
                <Kpi testid="kpi-pending" icon={Clock} label="Pending Fees" value={fmtMoney(dash.pending_fees)} tint="bg-amber-500/10 text-amber-600" />
                <Kpi testid="kpi-overdue" icon={AlertTriangle} label="Overdue Fees" value={fmtMoney(dash.overdue_fees)} tint="bg-rose-500/10 text-rose-600" />
              </div>

              {/* Due notifications */}
              <div className="mt-4 rounded-xl border border-border bg-card p-5" data-testid="due-notifications">
                <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold text-secondary"><Bell className="h-4 w-4 text-primary" /> Payment Reminders</h3>
                <div className="mb-3 flex flex-wrap gap-2">
                  <Badge className="border-0 bg-rose-100 text-rose-700">Overdue: {dueCount("overdue")}</Badge>
                  <Badge className="border-0 bg-primary/10 text-primary">Due Today: {dueCount("today")}</Badge>
                  <Badge className="border-0 bg-amber-100 text-amber-700">Tomorrow: {dueCount("tomorrow")}</Badge>
                  <Badge className="border-0 bg-sky-100 text-sky-700">This Week: {dueCount("this_week")}</Badge>
                  <Badge className="border-0 bg-slate-100 text-slate-600">Upcoming: {dueCount("upcoming")}</Badge>
                </div>
                <div className="space-y-2">
                  {["overdue", "today", "tomorrow"].flatMap((b) => (dues?.[b] || []).map((d) => ({ ...d, bucket: b }))).slice(0, 6).map((d) => (
                    <div key={d.student_id + d.bucket} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`due-${d.student_id}`}>
                      <div>
                        <p className="text-sm font-medium text-secondary">{d.student_name} <span className="font-mono text-xs text-slate-400">{d.student_code}</span></p>
                        <p className="text-xs text-muted-foreground">{fmtMoney(d.due_amount)} due {d.due_date} · <span className={d.bucket === "overdue" ? "text-rose-600" : "text-amber-600"}>{d.bucket}</span></p>
                      </div>
                      {d.reminder_completed ? (
                        <Badge className="border-0 bg-accent/15 text-green-700"><CheckCircle2 className="mr-1 h-3 w-3" /> Done</Badge>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => markReminder(d.student_id)} data-testid={`complete-reminder-${d.student_id}`}>Mark done</Button>
                      )}
                    </div>
                  ))}
                  {["overdue", "today", "tomorrow"].every((b) => dueCount(b) === 0) && <p className="text-sm text-muted-foreground">No urgent dues. You're all caught up.</p>}
                </div>
              </div>

              {/* Revenue charts */}
              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <RevChart testid="chart-course-rev" title="Course-wise Revenue" data={dash.course_revenue} keyName="course" color="#2563EB" />
                <RevChart testid="chart-batch-rev" title="Batch-wise Revenue" data={dash.batch_revenue} keyName="batch" color="#22C55E" />
                <RevChart testid="chart-exec-rev" title="Executive-wise Revenue" data={dash.executive_revenue} keyName="executive" color="#8B5CF6" />
              </div>

              {/* Recent payments */}
              <div className="mt-4 rounded-xl border border-border bg-card p-5" data-testid="recent-payments">
                <h3 className="mb-3 font-display text-sm font-semibold text-secondary">Recent Payments</h3>
                {dash.recent_payments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {dash.recent_payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between border-b border-border pb-2 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-secondary">{p.student_name}</p>
                          <p className="text-xs text-muted-foreground">{p.receipt_number} · {p.payment_mode} · {p.payment_date}</p>
                        </div>
                        <span className="font-semibold text-accent">{fmtMoney(p.amount_received)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* PAYMENTS */}
        <TabsContent value="payments">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search student, receipt, txn..." className="pl-9" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} data-testid="payments-search" />
            </div>
            <Select value={filters.payment_mode} onValueChange={(v) => { setFilters((f) => ({ ...f, payment_mode: v })); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-44" data-testid="filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Modes</SelectItem>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" onClick={exportPayments} data-testid="export-payments-button"><Download className="mr-1.5 h-4 w-4" /> Export</Button>
          </div>

          <div className="rounded-xl border border-border bg-card">
            {loadingList ? (
              <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : result.items.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="payments-empty">
                <Receipt className="h-8 w-8" /><p>No payments found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead>Receipt</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead className="hidden md:table-cell">Course</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead className="hidden sm:table-cell">Mode</TableHead>
                      <TableHead className="hidden lg:table-cell">Date</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.items.map((p) => (
                      <TableRow key={p.id} data-testid={`payment-row-${p.id}`} className="hover:bg-slate-50 hover:text-slate-900">
                        <TableCell className="font-mono text-xs text-slate-500">{p.receipt_number}</TableCell>
                        <TableCell>
                          <div className="font-medium text-secondary">{p.student_name}</div>
                          <div className="text-xs text-muted-foreground">{p.student_code}</div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-slate-600">{p.course || "—"}</TableCell>
                        <TableCell className="font-semibold text-accent">{fmtMoney(p.amount_received)}</TableCell>
                        <TableCell className="hidden sm:table-cell"><Badge className="border-0 bg-slate-100 text-slate-600">{p.payment_mode}</Badge></TableCell>
                        <TableCell className="hidden lg:table-cell text-slate-600">{p.payment_date}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`payment-actions-${p.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setReceiptId(p.id)} data-testid={`receipt-${p.id}`}><Receipt className="mr-2 h-4 w-4" /> View receipt</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setEditing(p); setFormOpen(true); }} data-testid={`edit-payment-${p.id}`}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setDeleteTarget(p)} className="text-destructive" data-testid={`delete-payment-${p.id}`}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between border-t border-border px-4 py-3" data-testid="pagination">
                  <p className="text-sm text-muted-foreground">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, result.total)} of {result.total}</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page">Previous</Button>
                    <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">Next</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-full sm:w-64" data-testid="report-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REPORT_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => exportReport("excel")} data-testid="report-export-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
              <Button variant="outline" onClick={() => exportReport("pdf")} data-testid="report-export-pdf"><FileText className="mr-1.5 h-4 w-4" /> PDF</Button>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card">
            {loadingReport || !report ? (
              <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : report.rows.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="report-empty"><FileText className="h-8 w-8" /><p>No data for this report.</p></div>
            ) : (
              <div className="overflow-x-auto" data-testid="report-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      {Object.keys(report.rows[0]).map((k) => <TableHead key={k}>{k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.rows.map((r, i) => (
                      <TableRow key={i} className="hover:bg-slate-50 hover:text-slate-900">
                        {Object.entries(r).map(([k, v]) => (
                          <TableCell key={k} className={k === "amount" || k.includes("fee") || k.includes("paid") || k === "pending_amount" ? "font-medium text-secondary" : "text-slate-600"}>
                            {(k === "amount" || ["final_fee", "total_paid", "pending_amount"].includes(k)) && typeof v === "number" ? fmtMoney(v) : String(v)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <PaymentFormDialog open={formOpen} onOpenChange={setFormOpen} payment={editing} onSaved={refreshAll} />
      <ReceiptDialog paymentId={receiptId} open={Boolean(receiptId)} onOpenChange={(v) => { if (!v) setReceiptId(null); }} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-payment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this payment?</AlertDialogTitle>
            <AlertDialogDescription>Receipt {deleteTarget?.receipt_number} ({fmtMoney(deleteTarget?.amount_received)}) will be removed and the student's balance recalculated. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-payment">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-payment">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
